import { Box, CircularProgress, Grid, Paper, Typography, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  getAllResponses,
  getInformationReqTemplatesByJob,
  InformationReqResponse,
  saveInformationReqResponse,
  updateJobCandidateNotesStateStage,
  inviteShareCandidateUsersByInformation,
  getJobById,
} from 'wisestep-api';
import {
  WsButton,
  WsColors,
  WsDialog,
  WsTypoVariants,
  getDateUsingFormat,
  WsTypoColors,
  Button,
  formattedDateISO,
  WsTypography,
} from 'wisestep-base';

import { findSurveyResponse } from '../../..../../../../../components/SurveyBuilder/CommonResponse';
import { useDefaultSmtpEmail } from '../../../../../components/SurveyBuilder/SurveyCommonMethods';
import SurveyInfoByJobComponent from '../../../../../components/SurveyBuilder/SurveyInfoByJobComponent';
import { BlankTemplateStyledBox } from '../../../../../components/TemplateStyled/CommonTemplateStled';
import { useWorkspaceContext } from '../../../../../context';
import { useUserContext } from '../../../../../context/UserContext';
import { useCandidateViewData } from '../../../../../features/candidateLayout/utils/CandidateViewContext';
import { informationReqAmplitudeEvents } from '../../../../../features/conversationWidget/utils/rtrHelper';
import { saveMapping } from '../../../../../features/conversationWidget/utils/saveMapping';
import NoInformationRequest from '../../../../../images/NoInformationRequest.svg';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import { InformationCategoryMappingDialog } from '../../../../conversationWidget/components/InformationCategoryMappingDialog';
import ShareCandidateByJob from '../../../../conversationWidget/components/ShareCandidateByJob';
import { failed, success, warning } from '../../.././../../utils/NotificationMessages';
import { interViewerPayloadHelper } from '../CandidateEvaluation/CandidateEvaluation';

interface survayResponseData {
  ques: string;
  ans: string;
}

interface JobCandidateInteractionTemp {
  id: string;
  applicationStage: string;
  applicationState: string;
  candidateNotes: { createdAt: string; createdBy?: string; notes: string }[];
  stageAndStateActions?: {
    stage: string;
    state: string;
    id: string;
    updatedBy: string;
    updatedOn: string;
  }[];
  stageDate?: string | null;
  candidateId: string;
  name: string;
  page: number;
}

const jobCandidateInteraction: JobCandidateInteractionTemp = {
  id: '',
  applicationStage: '',
  applicationState: '',
  candidateNotes: [{ notes: '', createdBy: '', createdAt: '' }],
  stageAndStateActions: [
    {
      stage: '',
      state: '',
      id: '',
      updatedBy: '',
      updatedOn: new Date().toString(),
    },
  ],
  candidateId: '',
  name: '',
  page: 0,
};

const StyledTypography = styled(Typography)({
  color: WsColors.TheBlues400,
  fontWeight: 'bold',
});

export const CandidateInformationRequest = ({ candidate, isMapping = false }) => {
  const userContext = useUserContext();
  const workspace = useWorkspaceContext();
  const { candidateViewData } = useCandidateViewData();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [templateJsonObject, setTemplateJsonObject] = useState('');
  const [informationJobId, setInformationJobId] = useState<string>('');
  const [selectedInfoReqResponsesId, setSelectedInfoReqResponsesId] = useState<string>('');
  const [mappedTemplates, setMappedTemplates] = useState<any[]>();
  const [infoReqResponses, setInfoReqResponses] = useState<InformationReqResponse[]>();
  const [shareCandidateByyJob, setShareCandidateByJob] = useState<boolean>(false);
  const [editMode, setEditMode] = useState(true);
  const [responseData, setResponseData] = useState<survayResponseData[]>();
  const [showViewSurveyResponse, setShowViewSurveyResponse] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { defaultSmtpEmailValue, preferredProviderId } = useDefaultSmtpEmail();

  const fetchMappedTemplates = async () => {
    try {
      const templatesResponse = await getInformationReqTemplatesByJob(candidate.jobId ?? '');
      const data = templatesResponse.data;
      setMappedTemplates(data);

      const responsesResponse = await getAllResponses(
        candidate.candidateId + '_' + candidate.jobId,
        candidate.jobId,
        candidate.recruiterId ?? ''
      );
      const NonRtrData = responsesResponse.data.filter(
        ({ informationReqTemplate }) => !informationReqTemplate.rtrTemplate
      );
      setInfoReqResponses(NonRtrData);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    fetchMappedTemplates();
    setShowViewSurveyResponse(false);
    setResponseData([]);
  }, []);

  const handleCloseDialog = async () => {
    setOpenDialog(false);
    await fetchMappedTemplates();
  };

  const handleSendRequest = (infoReqResponse) => {
    setIsLoading(true);
    setSelectedInfoReqResponsesId(infoReqResponse.id);
    setInformationJobId(infoReqResponse.id);
    setShareCandidateByJob(true);
    setIsLoading(false);
    informationReqAmplitudeEvents('info_request_sent', 'Information Request sent', workspace);
    informationReqAmplitudeEvents(
      'info_request_delivered',
      'Information Request delivered',
      workspace
    );
  };

  const fullUrl = `${window.location.href}&corphiringTemplate=informationrequestTemplate`;

  const callSendReminder = async (infoReqResponse) => {
    const payload = { ...interViewerPayloadHelper };
    payload.evalTemplateId = infoReqResponse?.informationReqTemplate.id;
    payload.interactionId = infoReqResponse?.interactionId;
    payload.jobId = infoReqResponse?.jobId;
    payload.reshareEmailId = infoReqResponse?.recruiterEmail;
    try {
      await inviteShareCandidateUsersByInformation({
        subject: `Information Required for ${candidate?.mappedJobTitle ?? ''}`,
        body: '',
        interactionId: candidateViewData?.candidateId ?? '',
        jobId: candidate?.jobId ?? '',
        sharedCandidateName: candidate?.name ?? '',
        sharedJobTitle: candidate?.mappedJobTitle ?? '',
        sharedJobLocation: candidate?.mappedJobLocation ?? '',
        sharedCandidateEmail: candidate?.email ?? '',
        sharedJobCode: candidate?.jobCode ?? '',
        informationTemplateId: infoReqResponse.id ?? '',
        isRTR: false,
        recruiterEmail: defaultSmtpEmailValue ?? '',
        logoUrl: '',
        isMapping: isMapping,
        otherInfo: [{ candidateProfileLink: fullUrl }],
        preferredProviderId: preferredProviderId,
      });
      setNotification(success(`Reminder has been shared successfully !!`));
    } catch (error) {
      setNotification(warning(`Oops , error`));
      console.error('error', error);
    }
  };

  const handleAddResponse = (infoReqResponse) => {
    setSelectedInfoReqResponsesId(infoReqResponse.id);
    setShowSurvey(true);
    setTemplateJsonObject(
      JSON.parse(JSON.stringify(infoReqResponse.informationReqTemplate.category))
    );
    setEditMode(true);
  };

  const updateNoteData = async (tempNode: string, recruitereFilled: boolean) => {
    const recruiterName = userContext?.userDTO?.displayName ?? '';
    const currentJob = candidateViewData?.candidate?.allCandidateSubmissions?.filter(
      (submssionObj) => submssionObj?.jobCode === candidate.jobCode
    );
    if (isMapping) {
      const objectInformationRequested = {
        downloadSource: 'NOT_SPECIFIED',
        recruiterActivity: {
          actions: recruitereFilled ? ['Information Submitted'] : ['Information Requested'],
          results: [],
        },
      };
      const res = await getJobById(candidate?.jobId);
      candidate.createdOn = formattedDateISO(new Date());
      const response = await saveMapping(objectInformationRequested, candidate, res?.data);
      if (response && response.status == 'success') {
        setNotification(success(`Note added successfully !!`));
      } else {
        setNotification(failed(`Note addition failed !!`));
      }
    } else {
      const { modifiedOn = '', id = '' } = currentJob?.[0] ? currentJob[0] : {};
      const { applicationStage = '', applicationState = '' } = candidate ?? {};
      jobCandidateInteraction.applicationStage = applicationStage ? applicationStage : '';
      jobCandidateInteraction.applicationState = applicationState ? applicationState : '';
      jobCandidateInteraction.id = candidate.id as string;
      jobCandidateInteraction.candidateNotes = [
        {
          notes: tempNode,
          createdBy: recruiterName as string,
          createdAt: formattedDateISO(new Date()),
        },
      ];
      jobCandidateInteraction.stageAndStateActions = [
        {
          stage: applicationStage ? applicationStage : '',
          state: applicationState ? applicationState : '',
          id: id ? id : '',
          updatedBy: (recruiterName as string) ?? '',
          updatedOn: modifiedOn ? modifiedOn : '',
        },
      ];
      await updateJobCandidateNotesStateStage(jobCandidateInteraction);
    }
  };

  const submitFeedback = async (ques, ans) => {
    try {
      const selectedInfoResponse = infoReqResponses?.filter(
        (infoObj) => infoObj.id === Number(selectedInfoReqResponsesId)
      );
      const recruiterName = userContext?.userDTO?.displayName ?? '';
      const tempNameVal = selectedInfoResponse?.[0]?.informationReqTemplate?.categoryName;
      const tempNode = `Information Request Response for template ${tempNameVal} was added by ${recruiterName}`;
      await saveInformationReqResponse({
        response: findSurveyResponse(ques, ans),
        id: Number(selectedInfoReqResponsesId),
        submittedBy: userContext?.userDTO?.email ?? '',
      });
      await updateNoteData(tempNode, true);
      await fetchMappedTemplates();
    } catch (error) {
      console.error('Error calling saveInformationReqResponse API:', error);
    }
    setShowSurvey(false);
  };

  const callBackSentRequest = () => {
    const selectedInfoResponse = infoReqResponses?.filter(
      (infoObj) => infoObj.id === Number(selectedInfoReqResponsesId)
    );
    const tempNameVal = selectedInfoResponse?.[0]?.informationReqTemplate?.categoryName;
    const nameList = candidate.name;
    const tempNode = `Information Request with template ${tempNameVal} was requested to ${nameList}`;
    setShareCandidateByJob(false);
    updateNoteData(tempNode, false);
    fetchMappedTemplates();
  };

  const handleViewResponse = (infoReqResponse) => {
    setEditMode(false);
    setShowSurvey(true);
    const keys = Object.keys(infoReqResponse.response);
    const feedbackArray = keys.map((ques) => ({
      ques,
      ans: infoReqResponse.response[ques],
    }));
    setTemplateJsonObject(
      JSON.parse(JSON.stringify(infoReqResponse.informationReqTemplate.category))
    );

    setResponseData(feedbackArray);
    setShowViewSurveyResponse(true);
  };

  const showBackInfoList = () => {
    setShowSurvey(false);
    setShowViewSurveyResponse(false);
    setResponseData([]);
  };

  const handleBackToList = () => {
    showBackInfoList();
  };

  return (
    <>
      {showSurvey ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              mb: 2,
              mt: 2,
              mr: 4,
            }}
          >
            <Button variant="outlined" sx={{ alignSelf: 'flex-end' }} onClick={handleBackToList}>
              Back to list
            </Button>
          </Box>
          <SurveyInfoByJobComponent
            surveyJson={templateJsonObject}
            feedbackDataBack={submitFeedback}
            callShowBackInfoList={showBackInfoList}
            isEditMode={editMode}
            responseData={responseData}
            survayResponse={showViewSurveyResponse}
            showBackButton={false}
          />
        </>
      ) : (
        <>
          <Grid container spacing={2} p={2}>
            {infoReqResponses && infoReqResponses.length > 0 && (
              <Grid item xs={12}>
                <StyledTypography variant={WsTypoVariants.SUB_TITLE}>
                  {`Request in Job (${infoReqResponses ? infoReqResponses.length : 0})`}
                </StyledTypography>
              </Grid>
            )}
            <Grid item xs={12}>
              {infoReqResponses &&
                (infoReqResponses.length > 0 ? (
                  infoReqResponses?.map((infoReqResponse, index: number) => (
                    <Paper variant="outlined" sx={{ borderRadius: 2, p: 2.5, mt: 1 }} key={index}>
                      <Grid container alignItems="center" justifyContent="space-between">
                        <Grid item>
                          <WsTypography variant={WsTypoVariants.TITLE} bold>
                            {infoReqResponse.informationReqTemplate?.categoryName}
                          </WsTypography>
                        </Grid>
                        <Grid item>
                          <Grid container alignItems="center" spacing={2}>
                            {!infoReqResponse.submittedBy ? (
                              <Grid item>
                                <WsButton
                                  variant="outlined"
                                  size="small"
                                  onClick={() => handleAddResponse(infoReqResponse)}
                                >
                                  Add Candidate Info
                                </WsButton>
                              </Grid>
                            ) : (
                              <Grid item>
                                <WsButton
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleViewResponse(infoReqResponse)}
                                >
                                  View Information
                                </WsButton>
                              </Grid>
                            )}
                            {!infoReqResponse.sendRequest && !infoReqResponse.submittedBy && (
                              <Grid item>
                                <WsButton
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleSendRequest(infoReqResponse)}
                                  disabled={isLoading}
                                  endIcon={
                                    isLoading ? (
                                      <CircularProgress size={17} color="secondary" />
                                    ) : (
                                      <></>
                                    )
                                  }
                                >
                                  {isLoading ? 'Saving...' : 'Send Request'}
                                </WsButton>
                              </Grid>
                            )}
                            {infoReqResponse?.sendRequest && !infoReqResponse?.submittedBy && (
                              <Grid item>
                                <WsButton
                                  variant="outlined"
                                  size="small"
                                  onClick={() => callSendReminder(infoReqResponse)}
                                >
                                  Send Reminder
                                </WsButton>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                      <Box mt={1} p={1} bgcolor={WsColors.White} borderRadius={2}>
                        <Grid container justifyContent="space-between">
                          <Grid item xs={6}>
                            <Grid container alignItems="center">
                              <Box pr={2}>
                                <WsTypography
                                  variant={WsTypoVariants.BODY1}
                                  color={WsTypoColors.BLACK}
                                  bold
                                >
                                  Tagged by:
                                </WsTypography>
                              </Box>
                              <Grid>{infoReqResponse?.createdBy as string}</Grid>
                            </Grid>
                          </Grid>
                          <Grid item xs={6}>
                            <Grid container alignItems="center">
                              <Box pr={2}>
                                <WsTypography
                                  variant={WsTypoVariants.BODY1}
                                  color={WsTypoColors.BLACK}
                                  bold
                                >
                                  Tagged on:
                                </WsTypography>
                              </Box>
                              <Grid>
                                {getDateUsingFormat(
                                  infoReqResponse.createdOn as string,
                                  'DD-MMM-YY'
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Box>
                      {infoReqResponse.submittedBy && (
                        <Box p={1} bgcolor={WsColors.White} borderRadius={2}>
                          <Grid container justifyContent="space-between">
                            <Grid item xs={6}>
                              <Grid container alignItems="center">
                                <Box pr={2}>
                                  <WsTypography
                                    variant={WsTypoVariants.BODY1}
                                    color={WsTypoColors.BLACK}
                                    bold
                                  >
                                    Responded By:
                                  </WsTypography>
                                </Box>
                                <Grid>{infoReqResponse.submittedBy}</Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={6}>
                              <Grid container alignItems="center">
                                <Box pr={2}>
                                  <WsTypography
                                    variant={WsTypoVariants.BODY1}
                                    color={WsTypoColors.BLACK}
                                    bold
                                  >
                                    Responded On:
                                  </WsTypography>
                                </Box>
                                <Grid>
                                  {infoReqResponse.submittedOn &&
                                    getDateUsingFormat(
                                      infoReqResponse.submittedOn.toString(),
                                      'DD-MMM-YY'
                                    )}
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  ))
                ) : (
                  <BlankTemplateStyledBox pt={15} pb={15} width={201}>
                    <img
                      src={NoInformationRequest}
                      width={140}
                      height={140}
                      alt="emptyInformationReq"
                    />
                    <Box pt={2}>
                      <WsTypography bold>
                        No Information request template is tagged to the job.
                      </WsTypography>
                    </Box>
                  </BlankTemplateStyledBox>
                ))}
            </Grid>
          </Grid>
        </>
      )}

      {openDialog && (
        <WsDialog openDialog={openDialog}>
          <InformationCategoryMappingDialog
            handleCloseDialog={handleCloseDialog}
            jobId={candidate.jobId}
            mappedTemplates={mappedTemplates}
          />
        </WsDialog>
      )}
      {shareCandidateByyJob && (
        <ShareCandidateByJob
          interactionId={candidate.candidiateId}
          jobTitle={candidate?.mappedJobTitle ?? ''}
          jobCode={candidate.jobCode ?? ''}
          jobId={candidate.jobId ?? ''}
          name={candidate.name}
          jobLocation={candidate.mappedJobLocation ?? ''}
          email={candidate.email}
          informationTemplateId={informationJobId}
          infoReqResponsesId={selectedInfoReqResponsesId}
          onSentRequest={callBackSentRequest}
          id={candidate.id}
          isMapping={isMapping}
          onClose={() => {
            setShareCandidateByJob(false);
          }}
          reset={() => {
            setShareCandidateByJob(false);
            setTimeout(() => setShareCandidateByJob(false), 600);
          }}
        />
      )}
    </>
  );
};
